import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Plus, X } from 'lucide-react';
import { useLayoutEffect, useRef, useState } from 'react';
import { TASK_LABEL_NAME_MAX_LENGTH, type TaskLabelColor } from '@till-erp/collab/values';
import { foldSearchText } from '@till-erp/platform/lists';
import { apiFetch, ApiProblem, newIdempotencyKey, type TaskLabel, type TaskLabelEntry } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';
import { cn } from '../lib/utils.ts';
import type { UndoOffer } from './undo-toast.tsx';
import { SearchMenu, SearchMenuNote } from './ui/search-menu.tsx';

/**
 * Labels an Aufgaben (Story 23.4, FR-153, EXPERIENCE §4.22; Entwurf Variante A „Linear“
 * `ux-till-erp/entwuerfe-2026-10-29-checkliste-labels/`): kleine Pill mit Farbpunkt und Name; Auswahl als Suchmenü mit
 * Häkchen, Anlegen direkt aus der Suche. Farben nur aus der festen Liste (Rot/Bernstein bleiben Dringend/Hoch/Prüfen).
 */

export const TASK_LABEL_UI_TEXTS = {
  section: 'Labels',
  chooser: 'Labels wählen',
  search: 'Label suchen oder anlegen',
  none: 'Label hinzufügen',
  add: 'Weiteres Label',
  empty: 'Noch keine Labels – Namen tippen und anlegen.',
  noMatch: 'Kein Label passt.',
  create: (name: string) => `Neues Label „${name}“ anlegen`,
  set: (name: string) => `Label „${name}“ gesetzt`,
  removed: (name: string) => `Label „${name}“ entfernt`,
  more: (n: number) => `+${n}`,
  moreLabel: (names: string[]) => `weitere Labels: ${names.join(', ')}`,
  remove: (name: string) => `Label „${name}“ entfernen`,
  pick: 'Label suchen',
  pickNone: 'keine',
  pickEmpty: 'Noch keine Labels. Anlegen im Reiter „Labels“ oder im Blatt einer Aufgabe.',
} as const;

/** Farbe → Tokens (Pill und Punkt im Menü). */
export const LABEL_LOOK: Record<TaskLabelColor, { pill: string; dot: string }> = {
  gray: { pill: 'bg-fill text-ink-2', dot: 'bg-ink-3' },
  blue: { pill: 'bg-info-bg text-info', dot: 'bg-info' },
  navy: { pill: 'bg-brand-tile text-brand-tile-fg', dot: 'bg-brand' },
  green: { pill: 'bg-ready-bg text-ready', dot: 'bg-ready' },
};

export const taskLabelsKey = ['tasks', 'labels'] as const;

/** Alle Labels (mit Version und Anzahl Aufgaben), nach Name. */
export function useTaskLabels() {
  const { getToken } = useSession();
  return useQuery({ queryKey: taskLabelsKey, queryFn: () => apiFetch<{ items: TaskLabelEntry[] }>(getToken, '/api/v1/tasks/labels') });
}

/** Pill „● Messe“: Farbpunkt und Name, 11,5 px (DESIGN Pill-Größe). */
export function LabelPill({ label, className }: { label: Pick<TaskLabel, 'name' | 'color'>; className?: string }) {
  return (
    <span
      data-label-pill={label.color}
      className={cn('inline-flex h-6 max-w-40 min-w-0 flex-none items-center gap-space-1 rounded-full px-space-2 text-pill font-medium whitespace-nowrap', LABEL_LOOK[label.color].pill, className)}
    >
      <span aria-hidden className="size-1.5 flex-none rounded-full bg-current opacity-75" />
      <span className="truncate">{label.name}</span>
    </span>
  );
}

/** Pills an Zeile und Karte: höchstens `max` (Standard 3), danach „+2“. */
export function LabelPills({ labels, max = 3, className }: { labels: TaskLabel[]; max?: number; className?: string }) {
  if (labels.length === 0) return null;
  const shown = labels.length > max ? labels.slice(0, max) : labels;
  const rest = labels.slice(shown.length);
  return (
    <span className={cn('inline-flex min-w-0 flex-wrap items-center gap-space-1', className)} data-testid="label-pills">
      <span className="sr-only">Labels:</span>
      {shown.map((l) => (
        <LabelPill key={l.id} label={l} />
      ))}
      {rest.length > 0 ? (
        <span className="inline-flex h-6 flex-none items-center rounded-full bg-fill px-space-2 text-pill font-medium text-ink-2" title={rest.map((l) => l.name).join(', ')}>
          <span aria-hidden>{TASK_LABEL_UI_TEXTS.more(rest.length)}</span>
          <span className="sr-only">{TASK_LABEL_UI_TEXTS.moreLabel(rest.map((l) => l.name))}</span>
        </span>
      ) : null}
    </span>
  );
}

function problemText(err: unknown): string {
  return err instanceof ApiProblem ? (Object.values(err.errors)[0] ?? err.detail) : 'Etwas ist schiefgelaufen.';
}

const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, 'de', { sensitivity: 'base' });

/**
 * Label-Wahl im Blatt: Klick auf die Pills öffnet das Suchmenü (am Handy als Blatt). Jede Wahl speichert sofort die ganze
 * Menge (`POST /tasks/{id}/labels`) und bietet 10 s „Rückgängig“; Enter auf „Neues Label „…“ anlegen“ legt es blau an und
 * setzt es. Mehrere schnelle Klicks laufen nacheinander (Warteschlange) mit der zuletzt gewählten Menge.
 */
export function LabelChooser({
  taskId,
  labels,
  onSaved,
  offerUndo,
}: {
  taskId: string;
  labels: TaskLabel[];
  /** Neuer Stand vom Server; der Aufrufer lädt Blatt und Liste neu. */
  onSaved: (labels: TaskLabel[]) => Promise<unknown>;
  offerUndo: (o: UndoOffer) => void;
}) {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  const all = useTaskLabels();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** Gewählte Menge, solange gespeichert wird (sofort sichtbar). */
  const [pending, setPending] = useState<TaskLabel[] | null>(null);
  /** Anlegen läuft: kein zweiter POST bei doppeltem Enter/Klick (sonst 409). */
  const [creating, setCreating] = useState(false);
  const creatingRef = useRef(false);
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const running = useRef(0);

  const current = pending ?? labels;
  /** Stand der letzten Darstellung: `create()` liest ihn nach dem Warten, damit eine Wahl währenddessen erhalten bleibt. */
  const currentRef = useRef(current);
  useLayoutEffect(() => {
    currentRef.current = current;
  });
  const chosen = new Set(current.map((l) => l.id));
  const known = all.data?.items ?? [];
  const typed = query.trim();
  const folded = foldSearchText(typed);
  const matches = known.filter((l) => folded === '' || foldSearchText(l.name).includes(folded));
  const exact = known.some((l) => l.name.toLocaleLowerCase('de') === typed.toLocaleLowerCase('de'));
  // Erst nach geladener Label-Liste anbieten: sonst stünde „Neues Label …“ auch für ein bestehendes Label da (→ 409).
  const canCreate = all.isSuccess && !creating && typed !== '' && !exact && typed.length <= TASK_LABEL_NAME_MAX_LENGTH;
  const count = matches.length + (canCreate ? 1 : 0);

  const post = <T,>(path: string, body: unknown) => apiFetch<T>(getToken, path, { method: 'POST', body, idempotencyKey: newIdempotencyKey() });

  /** Menge speichern (in der Warteschlange); `before` für „Rückgängig“. */
  const save = (next: TaskLabel[], before: TaskLabel[], text: string) => {
    setPending([...next].sort(byName));
    setError(null);
    running.current += 1;
    const run = async () => {
      try {
        const saved = await post<{ labels: TaskLabel[] }>(`/api/v1/tasks/${taskId}/labels`, {
          labelIds: next.map((l) => l.id),
          previousLabelIds: before.map((l) => l.id),
        });
        offerUndo({
          text,
          undo: () => {
            const back = async () => {
              const r = await post<{ labels: TaskLabel[] }>(`/api/v1/tasks/${taskId}/labels`, {
                labelIds: before.map((l) => l.id),
                previousLabelIds: next.map((l) => l.id),
              });
              await onSaved(r.labels);
            };
            const p = queue.current.then(back, back);
            queue.current = p.catch(() => undefined);
            return p;
          },
        });
        if (running.current === 1) await onSaved(saved.labels);
      } catch (err) {
        setError(problemText(err));
        if (running.current === 1) await onSaved(labels).catch(() => undefined);
        await queryClient.invalidateQueries({ queryKey: taskLabelsKey });
      } finally {
        running.current -= 1;
        if (running.current === 0) setPending(null);
      }
    };
    queue.current = queue.current.then(run, run);
  };

  const toggle = (label: TaskLabel) => {
    const before = current;
    const has = chosen.has(label.id);
    const next = has ? before.filter((l) => l.id !== label.id) : [...before, { id: label.id, name: label.name, color: label.color }];
    save(next, before, has ? TASK_LABEL_UI_TEXTS.removed(label.name) : TASK_LABEL_UI_TEXTS.set(label.name));
  };

  const create = async () => {
    const name = typed;
    if (name === '' || creatingRef.current) return;
    creatingRef.current = true;
    setCreating(true);
    setError(null);
    try {
      const made = await post<TaskLabelEntry>('/api/v1/tasks/labels', { name });
      setQuery('');
      void queryClient.invalidateQueries({ queryKey: taskLabelsKey });
      const now = currentRef.current;
      const next = now.some((l) => l.id === made.id) ? now : [...now, { id: made.id, name: made.name, color: made.color }];
      save(next, now, TASK_LABEL_UI_TEXTS.set(made.name));
    } catch (err) {
      setError(problemText(err));
      void queryClient.invalidateQueries({ queryKey: taskLabelsKey });
    } finally {
      creatingRef.current = false;
      setCreating(false);
    }
  };

  const choose = (i: number) => {
    const label = matches[i];
    if (label) toggle(label);
    else if (canCreate && i === matches.length) void create();
  };

  return (
    <SearchMenu
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) {
          setQuery('');
          setError(null);
        }
      }}
      label={TASK_LABEL_UI_TEXTS.chooser}
      testId="label-wahl"
      query={query}
      onQueryChange={setQuery}
      searchLabel={TASK_LABEL_UI_TEXTS.search}
      maxLength={TASK_LABEL_NAME_MAX_LENGTH}
      listLabel={TASK_LABEL_UI_TEXTS.section}
      multiselectable
      options={[
        ...matches.map((l) => ({ key: l.id, selected: chosen.has(l.id), content: <LabelOption label={l} chosen={chosen.has(l.id)} /> })),
        ...(canCreate
          ? [
              {
                key: 'neu',
                selected: false,
                testId: 'label-anlegen',
                className: 'text-brand',
                content: (
                  <>
                    <Plus size={14} strokeWidth={2.5} aria-hidden className="flex-none" />
                    <span className="min-w-0 truncate">{TASK_LABEL_UI_TEXTS.create(typed)}</span>
                  </>
                ),
              },
            ]
          : []),
      ]}
      onChoose={choose}
      trigger={(props) => (
        <button
          type="button"
          {...props}
          data-testid="aufgabe-labels"
          className="-mx-space-2 inline-flex min-h-9 cursor-pointer flex-wrap items-center gap-space-1 rounded-field px-space-2 py-space-1 text-left hover:bg-tile aria-expanded:bg-tile pointer-coarse:min-h-11"
        >
          {/* Name = sichtbarer Text mit vorangestelltem „Labels:“ (WCAG 2.5.3: sichtbarer Text im Namen). */}
          <span className="sr-only">{`${TASK_LABEL_UI_TEXTS.section}:`}</span>
          {current.map((l) => (
            <LabelPill key={l.id} label={l} />
          ))}
          {current.length === 0 ? (
            <span className="inline-flex items-center gap-space-1 text-row text-ink-3">
              <Plus size={14} strokeWidth={2} aria-hidden />
              {TASK_LABEL_UI_TEXTS.none}
            </span>
          ) : (
            <span aria-hidden className="grid size-6 place-items-center rounded-full text-ink-3">
              <Plus size={14} strokeWidth={2} />
            </span>
          )}
        </button>
      )}
    >
      {count === 0 && all.isSuccess && !creating ? <SearchMenuNote>{known.length === 0 ? TASK_LABEL_UI_TEXTS.empty : TASK_LABEL_UI_TEXTS.noMatch}</SearchMenuNote> : null}
      {error ? <SearchMenuNote alert>{error}</SearchMenuNote> : null}
    </SearchMenu>
  );
}

/** Zeile im Label-Menü: Farbpunkt, Name, Häkchen wenn gewählt. */
function LabelOption({ label, chosen }: { label: Pick<TaskLabel, 'name' | 'color'>; chosen: boolean }) {
  return (
    <>
      <span aria-hidden className={cn('size-2.5 flex-none rounded-full', LABEL_LOOK[label.color].dot)} />
      <span className="min-w-0 flex-1 truncate">{label.name}</span>
      {chosen ? <Check size={16} strokeWidth={2.5} aria-hidden className="flex-none text-brand" /> : null}
    </>
  );
}

/**
 * Labels wählen ohne zu speichern (Story 23.12): Vorlagen-Editor und Blatt „Neue Aufgabe“. Pills mit „×“, „+“ öffnet
 * dasselbe Suchmenü wie `LabelChooser`; die Menge geht über `onChange` an den Aufrufer, der sie mit dem Formular sendet.
 */
export function LabelSelect({ labels, onChange, testId, menuTestId }: { labels: TaskLabel[]; onChange: (labels: TaskLabel[]) => void; testId: string; menuTestId: string }) {
  const all = useTaskLabels();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const chosen = new Set(labels.map((l) => l.id));
  const known = all.data?.items ?? [];
  const folded = foldSearchText(query.trim());
  const matches = known.filter((l) => folded === '' || foldSearchText(l.name).includes(folded));
  const toggle = (l: TaskLabel) => onChange(chosen.has(l.id) ? labels.filter((x) => x.id !== l.id) : [...labels, { id: l.id, name: l.name, color: l.color }]);
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-space-1" data-testid={testId}>
      {labels.map((l) => (
        <span key={l.id} className={cn('inline-flex h-7 items-center gap-space-1 rounded-full pr-space-1 pl-space-2 text-pill font-medium', LABEL_LOOK[l.color].pill)}>
          <span aria-hidden className="size-1.5 flex-none rounded-full bg-current opacity-75" />
          <span className="max-w-40 truncate">{l.name}</span>
          <button
            type="button"
            aria-label={TASK_LABEL_UI_TEXTS.remove(l.name)}
            onClick={() => onChange(labels.filter((x) => x.id !== l.id))}
            className="grid size-5 cursor-pointer place-items-center rounded-full opacity-70 hover:opacity-100 pointer-coarse:size-8"
          >
            <X size={12} strokeWidth={2.5} aria-hidden />
          </button>
        </span>
      ))}
      {labels.length === 0 ? <span className="text-row text-ink-3">{TASK_LABEL_UI_TEXTS.pickNone}</span> : null}
      <SearchMenu
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) setQuery('');
        }}
        label={TASK_LABEL_UI_TEXTS.none}
        testId={menuTestId}
        query={query}
        onQueryChange={setQuery}
        searchLabel={TASK_LABEL_UI_TEXTS.pick}
        listLabel={TASK_LABEL_UI_TEXTS.section}
        multiselectable
        options={matches.map((l) => ({ key: l.id, selected: chosen.has(l.id), content: <LabelOption label={l} chosen={chosen.has(l.id)} /> }))}
        onChoose={(i) => {
          const l = matches[i];
          if (l) toggle(l);
        }}
        trigger={(props) => (
          <button
            type="button"
            {...props}
            aria-label={TASK_LABEL_UI_TEXTS.none}
            title={TASK_LABEL_UI_TEXTS.none}
            className="grid size-8 cursor-pointer place-items-center rounded-full border border-dashed border-chip-line text-ink-3 hover:border-field-line hover:text-ink aria-expanded:border-brand aria-expanded:text-brand pointer-coarse:size-11"
          >
            <Plus size={14} strokeWidth={2} aria-hidden />
          </button>
        )}
      >
        {all.isSuccess && matches.length === 0 ? <SearchMenuNote>{known.length === 0 ? TASK_LABEL_UI_TEXTS.pickEmpty : TASK_LABEL_UI_TEXTS.noMatch}</SearchMenuNote> : null}
      </SearchMenu>
    </div>
  );
}
